// Pure logic for the searchable SKU picker (SkuSearchSelect). Kept React-free
// so the bug-prone matching/labeling/highlight logic is unit-testable in the
// node vitest environment (same convention as labels.ts / format.ts).

import type { InventorySkuRow } from '@gutterinv/shared';

/** The closed-state / option label — byte-identical to the legacy native
 *  <select> <option> text ("CODE — Name (variant) [unit]"), so swapping the
 *  control in is a visual no-op. */
export function formatSkuLabel(
  s: Pick<InventorySkuRow, 'code' | 'name' | 'variant' | 'unit'>,
): string {
  return `${s.code} — ${s.name}${s.variant ? ` (${s.variant})` : ''} [${s.unit}]`;
}

/**
 * Filter + rank SKUs for a free-text query.
 * - empty / whitespace query → the full list UNCHANGED (preserves caller order,
 *   so the open-with-no-query view matches today's native dropdown order).
 * - matches case-insensitively against code, name, variant, categoryName, partNo —
 *   the same fields InventoryPage searches.
 * - ranks: code-prefix (0) < code-substring (1) < name-prefix (2) <
 *   any-other-field substring (3); stable within a tier (preserves input order).
 */
export function filterSkus(
  skus: InventorySkuRow[],
  query: string,
): InventorySkuRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return skus;
  const ranked: Array<{ s: InventorySkuRow; rank: number; i: number }> = [];
  skus.forEach((s, i) => {
    const code = s.code.toLowerCase();
    const name = s.name.toLowerCase();
    const variant = s.variant?.toLowerCase() ?? '';
    const cat = s.categoryName.toLowerCase();
    const partNo = s.partNo?.toLowerCase() ?? '';
    let rank = -1;
    if (code.startsWith(q)) rank = 0;
    else if (code.includes(q)) rank = 1;
    else if (name.startsWith(q)) rank = 2;
    else if (name.includes(q) || variant.includes(q) || cat.includes(q) || partNo.includes(q)) rank = 3;
    if (rank >= 0) ranked.push({ s, rank, i });
  });
  ranked.sort((a, b) => a.rank - b.rank || a.i - b.i);
  return ranked.map((r) => r.s);
}

/**
 * The label shown on the closed trigger.
 * - no value → '' (the placeholder shows instead).
 * - value present + found in the list → its formatted label.
 * - value present but NOT in the list (inactive/deleted SKU seeded from a prior
 *   quote or field estimate) → a fallback string. The component KEEPS the value
 *   and renders this; it must never auto-clear an unrenderable selection.
 */
export function resolveSelectedLabel(
  skus: InventorySkuRow[],
  value: string,
  unavailableLabel = 'Unavailable SKU',
): string {
  if (!value) return '';
  const found = skus.find((s) => s.skuId === value);
  return found ? formatSkuLabel(found) : unavailableLabel;
}
